import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { money, currentQuarter, financialYear, fmtDate } from "../lib/format";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button } from "../components/ui/button";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import { FileText, CheckCircle2, Printer, History, Download, AlertCircle } from "lucide-react";

const Row = ({ code, label, value, bold, accent }) => (
  <div className={`flex items-center justify-between px-4 py-3 border-b border-border last:border-0 ${bold ? "bg-secondary/40" : ""}`}
    data-testid={`bas-row-${code}`}>
    <div className="flex items-center gap-3">
      <span className="num text-xs w-8 text-muted-foreground font-semibold">{code}</span>
      <span className={`text-sm ${bold ? "font-semibold" : ""}`}>{label}</span>
    </div>
    <span className={`num text-sm ${bold ? "font-bold" : ""} ${accent || ""}`}>{money(value)}</span>
  </div>
);

export default function BASPage() {
  const [period, setPeriod] = useState("quarter");
  const q = currentQuarter();
  const fy = financialYear();
  const range = period === "quarter" ? q : fy;
  const [bas, setBas] = useState(null);
  const [history, setHistory] = useState([]);
  const [profile, setProfile] = useState(null);
  const [gsth, setGsth] = useState(null);

  const load = useCallback(() => {
    api.get("/reports/bas", { params: { date_from: range.from, date_to: range.to } }).then((r) => setBas(r.data));
    api.get("/bas/history").then((r) => setHistory(r.data));
    api.get("/profile").then((r) => setProfile(r.data));
    api.get("/reports/gst-health", { params: { date_from: range.from, date_to: range.to } }).then((r) => setGsth(r.data));
  }, [range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  const generatePdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(18); doc.setTextColor(17, 62, 53);
    doc.text("Business Activity Statement", 14, 20);
    doc.setFontSize(10); doc.setTextColor(100);
    doc.text(`${profile?.business_name || ""}   ABN ${profile?.abn || "—"}`, 14, 27);
    doc.text(`Period: ${range.label}  (${range.from} to ${range.to})`, 14, 33);
    autoTable(doc, {
      startY: 40,
      head: [["Code", "Description", "Amount (AUD)"]],
      body: [
        ["G1", "Total sales (incl. GST)", money(bas.G1_total_sales)],
        ["G2", "Export sales", money(bas.G2_export_sales)],
        ["G3", "Other GST-free sales", money(bas.G3_gst_free_sales)],
        ["G10", "Capital purchases", money(bas.G10_capital_purchases)],
        ["G11", "Non-capital purchases", money(bas.G11_non_capital_purchases)],
        ["1A", "GST on sales", money(bas["1A_gst_on_sales"])],
        ["1B", "GST on purchases", money(bas["1B_gst_on_purchases"])],
        ["W1", "Total salary & wages", money(bas.W1_total_wages)],
        ["W2", "PAYG withheld", money(bas.W2_payg_withheld)],
        ["8A", `Net GST (${bas.result})`, money(bas.amount)],
      ],
      theme: "grid",
      headStyles: { fillColor: [17, 62, 53] },
      styles: { fontSize: 10 },
    });
    const y = (doc.lastAutoTable?.finalY || 120) + 12;
    doc.setFontSize(13); doc.setTextColor(17, 62, 53);
    doc.text(`Total ${bas.total_result} to ATO:  ${money(bas.total_amount)}`, 14, y);
    doc.setFontSize(8); doc.setTextColor(130);
    doc.text("Generated by KangarooLedger. Not an official ATO lodgement.", 14, y + 8);
    doc.save(`BAS-${range.label.replace(/[^a-zA-Z0-9]/g, "-")}.pdf`);
    toast.success("BAS PDF downloaded");
  };

  const lodge = async () => {
    await api.post("/bas/lodge", {
      period_label: range.label,
      date_from: range.from,
      date_to: range.to,
      figures: bas,
    });
    toast.success("BAS lodgement recorded");
    load();
  };

  if (!bas) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Business Activity Statement</h1>
          <p className="text-muted-foreground text-sm mt-1">ATO-formatted GST calculation · {range.label}</p>
        </div>
        <div className="flex gap-2">
          <div className="inline-flex rounded-lg border border-border p-1 bg-card">
            <button onClick={() => setPeriod("quarter")} data-testid="bas-period-quarter"
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${period === "quarter" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Quarter</button>
            <button onClick={() => setPeriod("year")} data-testid="bas-period-year"
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${period === "year" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Full year</button>
          </div>
          <Button variant="outline" onClick={generatePdf} data-testid="bas-pdf-btn" className="gap-2"><Download className="w-4 h-4" /> PDF</Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="bas-print-btn" className="gap-2"><Printer className="w-4 h-4" /> Print</Button>
        </div>
      </div>

      <Tabs defaultValue="summary">
        <TabsList data-testid="bas-tabs">
          <TabsTrigger value="summary" data-testid="tab-summary">Summary</TabsTrigger>
          <TabsTrigger value="worksheet" data-testid="tab-worksheet">Worksheet</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-5">
          {gsth && gsth.count > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 mb-4" data-testid="gst-health-panel">
              <h3 className="font-heading font-semibold mb-1 flex items-center gap-2 text-amber-800">
                <AlertCircle className="w-4 h-4" /> GST health check — {gsth.count} item(s) to review before lodging
              </h3>
              <p className="text-xs text-amber-700 mb-3">{gsth.high} high · {gsth.medium} medium · {gsth.low} low</p>
              <div className="space-y-1.5 max-h-56 overflow-y-auto">
                {gsth.issues.slice(0, 20).map((i) => (
                  <div key={i.id + i.type} className="flex items-center justify-between text-sm bg-white/70 rounded-lg px-3 py-2 border border-amber-100" data-testid={`gst-issue-${i.type}`}>
                    <div className="min-w-0">
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded mr-2 ${i.severity === "high" ? "bg-rose-100 text-rose-700" : i.severity === "medium" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{i.label}</span>
                      <span className="text-muted-foreground">{i.description}</span>
                    </div>
                    <span className="num text-xs text-muted-foreground shrink-0 ml-2">{money(i.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {gsth && gsth.count === 0 && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 mb-4 flex items-center gap-2 text-emerald-800 text-sm font-medium" data-testid="gst-health-clear">
              <CheckCircle2 className="w-4 h-4" /> GST health check passed — no issues found for this period.
            </div>
          )}
          <div className="rounded-2xl border border-border bg-card p-5 mb-4" data-testid="bas-obligations-card">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Quarter obligations (auto-draft)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Net GST", value: bas.amount, sub: bas.result },
                { label: "PAYG (W2)", value: bas.W2_payg_withheld, sub: "withheld" },
                { label: "Super (SG)", value: bas.super_payable, sub: "payable" },
                { label: "Total obligations", value: bas.obligations_total, sub: "this period", bold: true },
              ].map((o) => (
                <div key={o.label} className={`rounded-xl px-4 py-3 ${o.bold ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                  <div className={`text-xs uppercase tracking-wider font-mono ${o.bold ? "text-primary-foreground/60" : "text-muted-foreground"}`}>{o.label}</div>
                  <div className="num text-lg font-bold mt-1">{money(o.value)}</div>
                  <div className={`text-xs capitalize ${o.bold ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{o.sub}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-border bg-card p-5 md:col-span-2">
              <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> GST amounts</h3>
              <div className="rounded-xl border border-border overflow-hidden">
                <Row code="1A" label="GST on sales (collected)" value={bas["1A_gst_on_sales"]} accent="text-sky-700" />
                <Row code="1B" label="GST on purchases (credits)" value={bas["1B_gst_on_purchases"]} accent="text-amber-700" />
                <Row code="W1" label="Total salary & wages" value={bas.W1_total_wages} />
                <Row code="W2" label="PAYG withheld" value={bas.W2_payg_withheld} accent="text-primary" />
                <Row code="8A" label={bas.result === "payable" ? "Net GST payable" : "Net GST refund"} value={bas.amount} bold accent={bas.result === "payable" ? "text-rose-600" : "text-emerald-600"} />
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-primary text-primary-foreground p-5 flex flex-col justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider font-mono text-primary-foreground/60">Total to ATO</div>
                <div className="num text-3xl font-bold mt-2" data-testid="bas-total-amount">{money(bas.total_amount)}</div>
                <div className="text-sm text-primary-foreground/70 mt-1 capitalize">{bas.total_result === "payable" ? "Payable to ATO" : "Refund from ATO"}</div>
                <div className="text-xs text-primary-foreground/50 mt-2">GST {money(bas.amount)} + PAYG {money(bas.W2_payg_withheld)}</div>
              </div>
              <Button onClick={lodge} data-testid="lodge-bas-btn"
                className="mt-4 bg-primary-foreground text-primary hover:bg-primary-foreground/90 gap-2">
                <CheckCircle2 className="w-4 h-4" /> Record lodgement
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="worksheet" className="mt-5">
          <div className="rounded-2xl border border-border bg-card p-5 max-w-2xl">
            <h3 className="font-heading font-semibold mb-3">GST calculation worksheet</h3>
            <div className="rounded-xl border border-border overflow-hidden">
              <Row code="G1" label="Total sales (incl. GST)" value={bas.G1_total_sales} />
              <Row code="G2" label="Export sales" value={bas.G2_export_sales} />
              <Row code="G3" label="Other GST-free sales" value={bas.G3_gst_free_sales} />
              <Row code="G10" label="Capital purchases (incl. GST)" value={bas.G10_capital_purchases} />
              <Row code="G11" label="Non-capital purchases (incl. GST)" value={bas.G11_non_capital_purchases} />
              <Row code="1A" label="GST on sales" value={bas["1A_gst_on_sales"]} bold accent="text-sky-700" />
              <Row code="1B" label="GST on purchases" value={bas["1B_gst_on_purchases"]} bold accent="text-amber-700" />
              <Row code="W1" label="Total salary, wages & other payments" value={bas.W1_total_wages} />
              <Row code="W2" label="Amount withheld from wages (PAYG)" value={bas.W2_payg_withheld} bold accent="text-primary" />
            </div>
            <p className="text-xs text-muted-foreground mt-3">GST is calculated as 1/11th of GST-inclusive standard-rated amounts, per ATO rules. Total payable also includes PAYG withheld (W2).</p>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-5">
          <div className="rounded-2xl border border-border bg-card divide-y divide-border">
            {history.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground text-sm">No lodgements recorded yet.</div>
            ) : history.map((h) => (
              <div key={h.id} className="flex items-center justify-between px-5 py-4" data-testid={`lodgement-${h.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary grid place-items-center"><History className="w-4 h-4" /></div>
                  <div>
                    <div className="font-medium text-sm">{h.period_label}</div>
                    <div className="text-xs text-muted-foreground font-mono">{h.reference} · {new Date(h.lodged_at).toLocaleDateString("en-AU")}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`num font-semibold ${h.figures?.result === "payable" ? "text-rose-600" : "text-emerald-600"}`}>{money(h.figures?.amount)}</div>
                  <div className="text-xs text-muted-foreground capitalize">{h.figures?.result}</div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
